import Immutable from 'immutable';

import {
	StaticLookup,
	RestLookup,
	initializeLookupStore,
	webapi,
	REPORTED_EXCEPTION,
	setBooleanTexts
} from 'ps-gui';
import { getPhrase } from './phrases';

let lookupMap = {};

export const EMPTY_LOOKUP = StaticLookup(Immutable.List([]));

export function getLookup(name) {
	return lookupMap[name] || EMPTY_LOOKUP;
}

export function getDefaultValue(lookup) {
	if (typeof lookup === 'string') {
		lookup = getLookup(lookup);
	}
	return lookup.getItems().getIn([0, 'key']);
}

export function setLookup(name, lookup) {
	lookupMap[name] = lookup;
}

export function setLookupFromItems(name, items) {
	lookupMap[name] = StaticLookup(Immutable.fromJS(items));
}

export function lookupExists(name) {
	return !!lookupMap[name];
}

initializeLookupStore(getLookup);

const restLookups = [
	'user',
	'partyIdName',
	'producerIdName',
	'nativeUser',
	'editableAffirmationDistributionPeriod',
	'submissionProcessedBy'
];

const comboRestLookups = {
	editableAffirmationDistributionPeriod: true
};

function createYearLookup() {
	let res = [];
	const maxYear = new Date().getFullYear();
	for (let year = maxYear; year >= '1950'; year--) {
		res.push({
			key: year,
			cells: [year]
		});
	}
	return StaticLookup(Immutable.fromJS(res));
}

function fromKeyList(name, keys, keyAsText) {
	return StaticLookup(Immutable.fromJS(
		keys.map(key => {
			return {
				key,
				text: keyAsText ? getPhrase(key) : null,
				cells: [name ? getPhrase(name + '.' + key) : key]
			};
		})
	));
}


function fromKeyList2(keys, keyAsText) {
	return StaticLookup(Immutable.fromJS(
		keys.map(key => {
			return {
				key,
				text: keyAsText ? getPhrase(key) : null,
				cells: [getPhrase(key)]
			};
		})
	));
}

async function loadStaticLookups() {
	let res = await webapi.execute('/api/proc/api_lookup/getStaticLookups');
	if (res === REPORTED_EXCEPTION) {
		return;
	}
	res.forEach(lu => {
		lookupMap[lu.name] = StaticLookup(Immutable.fromJS(lu.items));
	});
	// console.log('lookups', res);
}

function initializeRestLookups() {
	restLookups.forEach(rl => {
		let searchUrl;
		let processFilter;
		if (typeof rl === 'string') {
			searchUrl = '/api/proc/api_lookup/' + rl + 'Search';
			//processFilter = sqlFullText[rl] ? getSqlFulltextFilter : null;
			processFilter = null;
		} else {
			searchUrl = rl.searchUrl;
			processFilter = rl.processFilter;
			rl = rl.name;
		}
		lookupMap[rl] = RestLookup('/api/proc/api_lookup/' + rl + 'Text', searchUrl, processFilter, null, comboRestLookups[rl]);
	});

}

function TokenLookup(packageName, procedureName, fixedParams) {

	this.getRows = async function(filter) {
		let tokens;
		if (!filter || filter.trim() === '') {
			tokens = [];
		} else {
			tokens = filter.split(/\s/g);
			tokens = tokens.map((token, index) => {
				if (index === tokens.length - 1) {
					return '% ' + token + '%';
				} else {
					return '% ' + token + ' %';
				}
			});
		}
		let params = fixedParams || {};
		params.tokens = tokens;
		params.lookupMode = 'Y';
		params.filter = filter;
		let res = await webapi.execute('/api/proc/' + packageName + '/' + procedureName, params, null, true);
		if (res === REPORTED_EXCEPTION) {
			return Immutable.List([]);
		}
		return Immutable.fromJS(res);
	};

	this.getText = function(value) {
		if (!value) {
			return '';
		}
		const idx = value.indexOf('$');
		if (idx < 0) {
			return value;
		} else {
			return value.substring(idx + 1);
		}
	};

	this.withFixedParams = function(fixedParams) {
		return new TokenLookup(packageName, procedureName, fixedParams);
	};

	this.getMultiText = function(keys) {
		let resMap = {};
		keys.forEach(key => {
			resMap[key] = this.getText(key);
		});
		return resMap;
	};
}

export async function initializeLookups() {
	console.log('initializeLookups');
	lookupMap['year'] = createYearLookup();
	lookupMap['creatorRole.PER.CLS'] = fromKeyList('creatorRole.PER.CLS', ['D','E','F','G','H','I']);
	lookupMap['creatorRole.PER.POP'] = fromKeyList('creatorRole.PER.POP', ['A','B','C']);
	lookupMap['shareOrRoleOrNothing'] = fromKeyList('shareOrRoleOrNothing', ['S', 'R', 'N']);
	lookupMap['shareOrRole'] = fromKeyList('shareOrRoleOrNothing', ['S', 'R']);
	lookupMap['recordingProtocolGenre'] = fromKeyList('recordingProtocolGenre', ['POP', 'CLS']);
	lookupMap['genre'] = fromKeyList('genre', ['POP', 'CLS', 'SPK']);
	lookupMap['catalogPerformance'] = fromKeyList('catalogPerformance', ['1', '2', '3', '4', '5', '6', '7', '8', '9', '12']);
	lookupMap['recordingProtocolAuthorRole'] = fromKeyList('recordingProtocolAuthorRole', ['AH', 'AT']);
	lookupMap['recordingProtocolRecordingType'] = fromKeyList('recordingProtocolRecordingType', ['A', 'V']);
	lookupMap['distType'] = fromKeyList('distType', ['IP', 'NP'], true);
	lookupMap['audioVideo'] = fromKeyList('audioVideo', ['A', 'V']);
	lookupMap['ynBoolean'] = fromKeyList('ynBoolean', ['Y', 'N']);
	lookupMap['submissionResolution'] = fromKeyList('submissionResolution', ['Y', 'N']);
	lookupMap['affirmationAnomaly'] = fromKeyList2(['attendanceMismatch','runtimeMismatch','productionYearMismatch','sameDigitalSaleIncome','samePhysicalSaleIncome']);
	lookupMap['affirmationCurrency'] = fromKeyList(null, ['CZK', 'EUR', 'USD', 'GBP', 'PLN']);
	lookupMap['affirmationSubmissionItemType'] = fromKeyList('affirmationSubmissionItemType', ['B', 'C', 'S']);
	// lookupMap['affirmationConflictStatus'] = fromKeyList2(['resolved', 'passedToProducers', 'waitingForYourDecision', 'ignored']);
	await loadStaticLookups();
	initializeRestLookups();

	lookupMap['ufd'] = new TokenLookup('api_affirmations', 'getMatchingUfdRows');
	lookupMap['pressdata'] = new TokenLookup('api_affirmations', 'getMatchingPressdataRows');

	const ynBoolean = getLookup('ynBoolean');
	setBooleanTexts(ynBoolean.getText('Y'), ynBoolean.getText('N'));
}
